/* eslint-disable jsx-a11y/label-has-associated-control */
import { ChangeEvent, FormEvent } from "react";

import { Button, FileInput, Spinner } from "@ezmid/components-library";
import { t } from "i18next";
import {
  Control,
  FieldErrors,
  FieldValues,
  FieldPath,
  Path,
} from "react-hook-form";
import type { Options } from "react-select";

import { EntityCourseTranslation } from "../../../api/courses.generated";
import { FormInputField } from "../../../components/hookFormComponents/FormInputField";
import { MarkdownField } from "../../../components/hookFormComponents/MarkdownField";
import {
  Option,
  SelectInputField,
} from "../../../components/hookFormComponents/SelectInputField";
import { Tabs } from "../../../components/tabs/Tabs";
import { Optional } from "../../../enums/types";

const DEFAULT_IMAGE = "/exercise-avatar.jpeg";
type InitialValues = {
  defaultEnValues?: EntityCourseTranslation;
  defaultDeValues?: EntityCourseTranslation;
};
type Props<T extends FieldValues> = {
  handleSubmit: (event: FormEvent<HTMLFormElement>) => void;
  control: Control<T>;
  errors: FieldErrors;
  courseImage: string;
  onChangePicture: (e: ChangeEvent<HTMLInputElement>) => void;
  onSubmit: () => void;
  isImageLoading: boolean;
  lecturerOptions: Options<Option>;
  defaultLecturerValue: Optional<Option>;
  isSubmitButtonDisabled: boolean;
  initialValues?: InitialValues;
  defaultStatusValue?: Optional<Option>;
};

export function CourseDetailForm<T extends FieldValues>({
  handleSubmit,
  errors,
  courseImage,
  onChangePicture,
  onSubmit,
  isImageLoading,
  lecturerOptions,
  defaultLecturerValue,
  control,
  isSubmitButtonDisabled,
  initialValues,
  defaultStatusValue,
}: Props<T>) {
  const courseNameDataTabs = [
    {
      label: `${t("form.name.label")} DE`,
      content: (
        <div className="pt-2">
          <FormInputField
            key="courseNameDE"
            name={"courseNameDE" as Path<T>}
            htmlFor="courseNameDE"
            id="courseNameDE"
            type="text"
            control={control}
            error={errors.courseNameDE}
            placeholder={t("form.name.label")}
            defaultValue={initialValues?.defaultEnValues?.name}
          />
        </div>
      ),
    },
    {
      label: `${t("form.name.label")} EN`,
      content: (
        <div className="pt-2">
          <FormInputField
            key="courseNameEN"
            name={"courseNameEN" as Path<T>}
            htmlFor="courseNameEN"
            id="courseNameEN"
            type="text"
            control={control}
            error={errors.courseNameEN}
            placeholder={t("form.name.label")}
            defaultValue={initialValues?.defaultEnValues?.name}
          />
        </div>
      ),
    },
  ];

  const shortDescriptionDataTabs = [
    {
      label: `${t("form.shortDescription")} DE`,
      content: (
        <div className="pt-2">
          <FormInputField
            key="shortDescriptionDE"
            name={"shortDescriptionDE" as Path<T>}
            htmlFor="shortDescriptionDE"
            id="shortDescriptionDE"
            type="text"
            control={control}
            error={errors.shortDescriptionDE}
            placeholder={t("form.shortDescription")}
            defaultValue={initialValues?.defaultDeValues?.shortDescription}
          />
        </div>
      ),
    },
    {
      label: `${t("form.shortDescription")} EN`,
      content: (
        <div className="pt-2">
          <FormInputField
            key="shortDescriptionEN"
            name={"shortDescriptionEN" as Path<T>}
            htmlFor="shortDescriptionEN"
            id="shortDescriptionEN"
            type="text"
            control={control}
            error={errors.shortDescriptionEN}
            placeholder={t("form.shortDescription")}
            defaultValue={initialValues?.defaultEnValues?.shortDescription}
          />
        </div>
      ),
    },
  ];

  const goalDescriptionTabs = [
    {
      label: `${t("form.goalDescription")} DE`,
      content: (
        <MarkdownField
          key="goalDescriptionDE"
          name={"goalDescriptionDE" as FieldPath<T>}
          htmlFor="goalDescriptionDE"
          id="goalDescriptionDE"
          control={control}
          error={errors.goalDescriptionDE}
          placeholder={t("form.goalDescription")}
        />
      ),
    },
    {
      label: `${t("form.goalDescription")} EN`,
      content: (
        <MarkdownField
          key="goalDescriptionEN"
          name={"goalDescriptionEN" as FieldPath<T>}
          htmlFor="goalDescriptionEN"
          id="goalDescriptionEN"
          control={control}
          error={errors.goalDescriptionEN}
          placeholder={t("form.goalDescription")}
        />
      ),
    },
  ];

  const textDataTabs = [
    {
      label: `${t("form.description.label")} DE`,
      content: (
        <MarkdownField
          key="descriptionDE"
          name={"descriptionDE" as FieldPath<T>}
          htmlFor="descriptionDE"
          id="descriptionDE"
          control={control}
          error={errors.descriptionDE}
          placeholder={t("form.description.label")}
        />
      ),
    },
    {
      label: `${t("form.description.label")} EN`,
      content: (
        <MarkdownField
          key="descriptionEN"
          name={"descriptionEN" as FieldPath<T>}
          htmlFor="descriptionEN"
          id="descriptionEN"
          control={control}
          error={errors.descriptionEN}
          placeholder={t("form.description.label")}
        />
      ),
    },
  ];

  const structureTabs = [
    {
      label: `${t("form.structure")} DE`,
      content: (
        <MarkdownField
          key="structureDE"
          name={"structureDE" as FieldPath<T>}
          htmlFor="structureDE"
          id="structureDE"
          control={control}
          error={errors.structureDE}
          placeholder={t("form.structure")}
        />
      ),
    },
    {
      label: `${t("form.structure")} EN`,
      content: (
        <MarkdownField
          key="structureEN"
          name={"structureEN" as FieldPath<T>}
          htmlFor="structureEN"
          id="structureEN"
          control={control}
          error={errors.structureEN}
          placeholder={t("form.structure")}
        />
      ),
    },
  ];

  const targetGroupTabs = [
    {
      label: `${t("form.targetGroup")} DE`,
      content: (
        <div className="pt-2">
          <MarkdownField
            key="targetGroupDE"
            name={"targetGroupDE" as FieldPath<T>}
            htmlFor="targetGroupDE"
            id="targetGroupDE"
            control={control}
            error={errors.targetGroupDE}
            placeholder={t("form.targetGroup")}
            defaultValue={initialValues?.defaultDeValues?.targetGroup}
          />
        </div>
      ),
    },
    {
      label: `${t("form.targetGroup")} EN`,
      content: (
        <div className="pt-2">
          <MarkdownField
            key="targetGroupEN"
            name={"targetGroupEN" as FieldPath<T>}
            htmlFor="targetGroupEN"
            id="targetGroupEN"
            control={control}
            error={errors.targetGroupEN}
            placeholder={t("form.targetGroup")}
            defaultValue={initialValues?.defaultEnValues?.targetGroup}
          />
        </div>
      ),
    },
  ];
  return (
    <form onSubmit={handleSubmit} className="px-4 pt-2">
      <div className="flex w-full min-w-full">
        <div className="flex justify-center items-center w-3/12">
          {!isImageLoading ? (
            <div className="flex flex-col justify-center pr-2">
              <img
                src={courseImage || DEFAULT_IMAGE}
                alt="course"
                className=" rounded-md h-52 object-cover"
              />
            </div>
          ) : (
            <div className="flex w-full min-w-full justify-center">
              <Spinner
                isLoading={isImageLoading}
                type="fadeLoader"
                color="#38bdf8"
              />
            </div>
          )}
        </div>
        <div className="w-3/4">
          <SelectInputField
            name={"lecturerID" as Path<T>}
            htmlFor="lecturerID"
            label={t("app.course.lecturer")}
            options={lecturerOptions || []}
            control={control}
            defaultValue={defaultLecturerValue}
          />
          <div className="pt-2">
            <label className="block font-normal tmb-1 text-gray-500">
              {t("form.label.upload.picture")}
            </label>
            <FileInput inputSize="md" onChange={onChangePicture} />
          </div>
          <SelectInputField
            name={"status" as Path<T>}
            htmlFor="status"
            label={t("app.form.status")}
            options={[
              { value: "cs-active", label: t("form.status.active") },
              { value: "cs-preview", label: t("form.status.preview") },
              { value: "cs-draft", label: t("form.status.draft") },
            ]}
            control={control}
            defaultValue={defaultStatusValue || null}
            error={errors.status}
          />
        </div>
      </div>
      <Tabs tabsData={courseNameDataTabs} />
      <Tabs tabsData={shortDescriptionDataTabs} />
      <Tabs tabsData={textDataTabs} />
      <Tabs tabsData={goalDescriptionTabs} />
      <Tabs tabsData={structureTabs} />
      <Tabs tabsData={targetGroupTabs} />

      <div className="flex justify-end pb-4 ">
        <Button onClick={onSubmit} disabled={isSubmitButtonDisabled}>
          {t("button.save")}
        </Button>
      </div>
    </form>
  );
}
